// Phase 4 / P4c — Internal Business Health benchmark widget.
// Consumes GET /api/benchmarks/business_health. Shows cohort percentile
// context (p25 / p50 / p75) when sample_n >= 30, or an honest
// "still building" state when N < 30. Internal-only. No external data,
// no industry claims, no AI. Verbatim CEO-approved copy strings.
import { useEffect, useState } from "react";
import { getBusinessHealthBenchmark } from "../../lib/api/benchmarks";
import { logger } from "../../lib/logger";

export default function BenchmarkWidget({ score, testidPrefix }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getBusinessHealthBenchmark()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        logger.error("benchmark_load_failed", { status: e?.response?.status });
        if (!cancelled) setErr(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (err || !data) return null;

  const insufficient = data.insufficient_cohort || data.sample_n < 30;
  const userScore = typeof score === "number" ? score : null;

  return (
    <div
      className="sf-card"
      style={{ marginBottom: 28 }}
      data-testid={`${testidPrefix}-benchmark-card`}
    >
      <div className="sf-eyebrow" style={{ marginBottom: 12 }}>Benchmark</div>
      <p style={{ color: "var(--sf-offwhite)", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
        Where your Business Health sits relative to other SystemForge audits.
      </p>

      {insufficient ? (
        <div
          className="sf-mono sf-muted-text"
          style={{ fontSize: 13, lineHeight: 1.55 }}
          data-testid={`${testidPrefix}-benchmark-insufficient`}
        >
          Cohort still building — we publish benchmarks once N ≥ 30 audits are in.
        </div>
      ) : (
        <div data-testid={`${testidPrefix}-benchmark-percentiles`}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 18 }}>
            <BenchTile label="p25" value={data.p25} testid={`${testidPrefix}-benchmark-p25`} />
            <BenchTile label="p50 (median)" value={data.p50} testid={`${testidPrefix}-benchmark-p50`} />
            <BenchTile label="p75" value={data.p75} testid={`${testidPrefix}-benchmark-p75`} />
          </div>
          {userScore !== null && (
            <BenchPositionBar
              userScore={userScore}
              p25={data.p25}
              p50={data.p50}
              p75={data.p75}
              testid={`${testidPrefix}-benchmark-position`}
            />
          )}
        </div>
      )}

      <div
        className="sf-mono sf-muted-text"
        style={{ marginTop: 14, fontSize: 11, letterSpacing: "0.06em" }}
        data-testid={`${testidPrefix}-benchmark-disclosure`}
      >
        Based on N={data.sample_n} SystemForge audits to date.
      </div>
    </div>
  );
}

function BenchTile({ label, value, testid }) {
  return (
    <div className="sf-card-inset" data-testid={testid}>
      <div className="sf-mono sf-muted-text" style={{ fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>
        {label}
      </div>
      <div className="sf-score-number" style={{ fontSize: 32 }}>{value ?? "—"}</div>
      <div className="sf-mono sf-muted-text" style={{ marginTop: 2, fontSize: 11 }}>/ 100</div>
    </div>
  );
}

function BenchPositionBar({ userScore, p25, p50, p75, testid }) {
  // Position the user's marker along a 0–100 scale. p25/p50/p75 ticks
  // are rendered as muted gold marks; user's score is a brighter gold dot.
  const pct = Math.max(0, Math.min(100, userScore));
  return (
    <div data-testid={testid}>
      <div style={{
        position: "relative", height: 8, backgroundColor: "#22262B", borderRadius: 4,
      }}>
        {[p25, p50, p75].map((v, i) => (
          <div key={`tick-${i}`} style={{
            position: "absolute", left: `${v}%`, top: -2, width: 2, height: 12,
            backgroundColor: "var(--sf-gold-dim)",
          }} />
        ))}
        <div style={{
          position: "absolute", left: `calc(${pct}% - 6px)`, top: -3, width: 12, height: 14,
          borderRadius: 3, backgroundColor: "var(--sf-gold)",
        }} />
      </div>
      <div className="sf-mono sf-muted-text" style={{ fontSize: 11, marginTop: 6 }}>
        Your score: {userScore}
      </div>
    </div>
  );
}
